// Add (or stack onto) a `.transpose(±12)` effect so the voice plays an
// octave higher / lower. Only applies to voice.note (sample voices
// don't respect transpose meaningfully).
//
// If a `.transpose(N)` already exists in the chain, increment N rather
// than appending a second one — keeps the source clean across repeated
// invocations and matches how a user would manually edit.

import { newBlockId } from '../blocks/types';
import type { EditRecipe } from '../blocks/editContext';
import type { Effect, SimpleEffect } from '../blocks/types';
import type { Transform } from './types';

function makeTransposeTransform(
  id: string,
  verb: string,
  description: string,
  delta: number,
): Transform {
  return {
    id,
    category: 'tonal',
    verb,
    description,
    apply({ song, voiceName }) {
      const stmt = song.statements.find(
        (s) => s.kind === 'const' && s.name === voiceName,
      );
      if (!stmt || stmt.kind !== 'const') {
        return { ok: false, reason: `no const named "${voiceName}"` };
      }
      const expr = stmt.expr;
      if (expr.kind !== 'voice.note') {
        return {
          ok: false,
          reason: `"${voiceName}" is a sample voice — transpose only applies to note(…) voices`,
        };
      }
      const recipe: EditRecipe = (draft) => {
        const target = draft.statements.find(
          (s) => s.kind === 'const' && s.name === voiceName,
        );
        if (!target || target.kind !== 'const') return;
        const ex = target.expr;
        if (ex.kind !== 'voice.note') return;
        const existingIdx = ex.effects.findIndex(isSimpleNumericTranspose);
        if (existingIdx >= 0) {
          // `isSimpleNumericTranspose` already guarantees the shape:
          // SimpleEffect with one numeric `num` arg.
          const eff = ex.effects[existingIdx]! as SimpleEffect;
          const arg = eff.args[0]!;
          if (arg.kind !== 'numeric' || arg.value.kind !== 'num') return;
          const next = arg.value.value + delta;
          if (next === 0) {
            ex.effects.splice(existingIdx, 1);
          } else {
            eff.args[0] = {
              kind: 'numeric',
              value: { kind: 'num', value: next },
            };
          }
        } else {
          const fresh: SimpleEffect = {
            id: newBlockId(),
            kind: 'transpose',
            args: [{ kind: 'numeric', value: { kind: 'num', value: delta } }],
          };
          ex.effects.push(fresh);
        }
      };
      return {
        ok: true,
        recipe,
        summary: `${verb.toLowerCase()} ${voiceName}`,
      };
    },
  };
}

function isSimpleNumericTranspose(e: Effect): boolean {
  // `transpose` is a SimpleEffectKind, so e is necessarily a SimpleEffect.
  if (e.kind !== 'transpose') return false;
  if (e.args.length !== 1) return false;
  const arg = e.args[0]!;
  return arg.kind === 'numeric' && arg.value.kind === 'num';
}

export const transposeUpOctaveTransform: Transform = makeTransposeTransform(
  'transpose-up-octave',
  'Transpose up an octave',
  'Add or increment .transpose(+12) on a note voice.',
  12,
);

export const transposeDownOctaveTransform: Transform = makeTransposeTransform(
  'transpose-down-octave',
  'Transpose down an octave',
  'Add or increment .transpose(-12) on a note voice.',
  -12,
);
